import { useState, useEffect } from 'react';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import {
  Ship, Navigation, Anchor, MapPin, Clock, Gauge, Compass,
  ArrowRight, AlertTriangle, CheckCircle, Loader2, LocateFixed
} from 'lucide-react';

function VesselCard({ vessel, selected, onSelect }) {
  const speed = vessel.speed_knots ? parseFloat(vessel.speed_knots).toFixed(1) : '—';
  return (
    <Card
      onClick={() => onSelect(vessel.id)}
      data-testid={`vessel-card-${vessel.imo}`}
      className={`cursor-pointer transition-all rounded-sm shadow-none border ${
        selected ? 'border-[#0A2463] bg-[#0A2463]/[0.03]' : 'border-slate-200 bg-white hover:border-slate-300'
      }`}
    >
      <CardContent className="p-3">
        <div className="flex items-start justify-between">
          <div className="min-w-0 flex-1">
            <p className="text-xs font-bold text-slate-900 truncate">{vessel.name}</p>
            <p className="text-[10px] font-mono text-slate-400 mt-0.5">IMO {vessel.imo}</p>
          </div>
          <Badge
            variant="outline"
            className={`rounded-sm text-[9px] uppercase tracking-wider shrink-0 ml-2 ${
              vessel.status === 'active' ? 'border-green-300 text-green-700 bg-green-50' : 'border-slate-200 text-slate-500'
            }`}
          >
            {vessel.status || 'active'}
          </Badge>
        </div>
        <div className="flex items-center gap-3 mt-2 text-[10px] text-slate-500">
          <span className="flex items-center gap-0.5"><Gauge className="h-3 w-3" />{speed} kn</span>
          <span className="flex items-center gap-0.5"><MapPin className="h-3 w-3" />{vessel.destination || '—'}</span>
        </div>
        {vessel.flag && (
          <p className="text-[10px] text-slate-400 mt-1">{vessel.flag} &middot; {vessel.vessel_type} &middot; {vessel.capacity_cbm ? `${parseFloat(vessel.capacity_cbm).toLocaleString()} cbm` : ''}</p>
        )}
      </CardContent>
    </Card>
  );
}

function ETAResult({ eta }) {
  if (!eta || eta.error) return null;
  return (
    <Card className="bg-white border border-slate-200 rounded-sm shadow-none" data-testid="eta-result">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-semibold text-slate-800 flex items-center gap-2">
          <Clock className="h-4 w-4 text-[#0A2463]" />ETA Prediction
        </CardTitle>
      </CardHeader>
      <CardContent className="p-4 pt-0">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div>
            <p className="text-[10px] uppercase tracking-[0.2em] font-semibold text-slate-500">Destination</p>
            <p className="font-mono text-sm font-bold text-slate-900 mt-0.5">{eta.destination}</p>
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-[0.2em] font-semibold text-slate-500">Predicted ETA</p>
            <p className="font-mono text-sm font-bold text-slate-900 mt-0.5">
              {new Date(eta.predicted_eta).toLocaleDateString()} {new Date(eta.predicted_eta).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
            </p>
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-[0.2em] font-semibold text-slate-500">Confidence</p>
            <div className="flex items-center gap-1.5 mt-0.5">
              <div className="flex-1 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                <div
                  className={`h-full rounded-full ${eta.confidence_pct >= 80 ? 'bg-green-500' : eta.confidence_pct >= 60 ? 'bg-amber-500' : 'bg-red-500'}`}
                  style={{ width: `${eta.confidence_pct}%` }}
                />
              </div>
              <span className="font-mono text-xs font-bold text-slate-700">{eta.confidence_pct}%</span>
            </div>
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-[0.2em] font-semibold text-slate-500">Distance</p>
            <p className="font-mono text-sm font-bold text-slate-900 mt-0.5">{eta.distance_nm?.toLocaleString()} NM</p>
          </div>
        </div>
        <div className="flex gap-6 mt-3 text-xs text-slate-500 border-t border-slate-100 pt-3">
          <span>Transit: <b className="text-slate-700 font-mono">{eta.transit_hours}h</b></span>
          <span>Avg Speed: <b className="text-slate-700 font-mono">{eta.avg_speed_knots} kn</b></span>
          <span>Window: <b className="text-slate-700 font-mono">&plusmn;{eta.confidence_hours}h</b></span>
        </div>
      </CardContent>
    </Card>
  );
}

export default function VesselTrackingPage() {
  const [vessels, setVessels] = useState([]);
  const [terminals, setTerminals] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [etaPredictions, setEtaPredictions] = useState([]);
  const [etaResult, setEtaResult] = useState(null);
  const [etaLoading, setEtaLoading] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const [v, t, ep] = await Promise.all([
          api.get('/scheduling/vessels'),
          api.get('/scheduling/terminals'),
          api.get('/scheduling/eta-predictions'),
        ]);
        setVessels(v.data);
        setTerminals(t.data);
        setEtaPredictions(ep.data);
        if (v.data.length > 0) setSelectedId(v.data[0].id);
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    };
    load();
  }, []);

  const selectedVessel = vessels.find(v => v.id === selectedId);

  const handlePredictETA = async () => {
    if (!selectedId) return;
    setEtaLoading(true);
    setEtaResult(null);
    try {
      const { data } = await api.get(`/scheduling/vessels/${selectedId}/eta`);
      setEtaResult(data);
      const { data: ep } = await api.get('/scheduling/eta-predictions');
      setEtaPredictions(ep);
    } catch (e) { console.error(e); }
    finally { setEtaLoading(false); }
  };

  if (loading) {
    return (
      <div className="p-6 md:p-8 space-y-6" data-testid="vessel-tracking-loading">
        <Skeleton className="h-8 w-56" />
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          <div className="lg:col-span-3 space-y-2">{[1,2,3,4].map(i => <Skeleton key={i} className="h-24" />)}</div>
          <Skeleton className="lg:col-span-9 h-96" />
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 md:p-8 space-y-6" data-testid="vessel-tracking-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-3xl font-black tracking-tighter text-slate-950">Vessel Tracking</h1>
          <p className="text-sm text-slate-500 mt-1">Fleet overview, AIS positions & ETA predictions</p>
        </div>
        <Badge variant="outline" className="rounded-sm text-xs">{vessels.length} vessels</Badge>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* Left: Vessel List */}
        <div className="lg:col-span-3 space-y-2">
          <p className="text-[10px] uppercase tracking-[0.2em] font-semibold text-slate-500 px-1">
            Fleet ({vessels.length})
          </p>
          {vessels.map(v => (
            <VesselCard key={v.id} vessel={v} selected={v.id === selectedId} onSelect={setSelectedId} />
          ))}
        </div>

        {/* Right: Detail */}
        <div className="lg:col-span-9 space-y-4">
          {!selectedVessel ? (
            <Card className="bg-white border border-slate-200 rounded-sm shadow-none">
              <CardContent className="py-24 text-center">
                <Ship className="h-12 w-12 mx-auto text-slate-200 stroke-[1px]" />
                <p className="mt-4 text-sm text-slate-400">Select a vessel to view details</p>
              </CardContent>
            </Card>
          ) : (
            <>
              {/* Vessel Header */}
              <Card className="bg-white border border-slate-200 rounded-sm shadow-none">
                <CardContent className="p-4">
                  <div className="flex items-start justify-between">
                    <div>
                      <h2 className="text-lg font-bold text-slate-900">{selectedVessel.name}</h2>
                      <div className="flex flex-wrap gap-x-4 gap-y-1 mt-1.5 text-xs text-slate-500">
                        <span>IMO: <b className="font-mono text-slate-700">{selectedVessel.imo}</b></span>
                        <span>Flag: <b className="text-slate-700">{selectedVessel.flag}</b></span>
                        <span>Type: <b className="text-slate-700">{selectedVessel.vessel_type}</b></span>
                        <span>DWT: <b className="font-mono text-slate-700">{selectedVessel.dwt ? parseFloat(selectedVessel.dwt).toLocaleString() : '—'}</b></span>
                        <span>Capacity: <b className="font-mono text-slate-700">{selectedVessel.capacity_cbm ? `${parseFloat(selectedVessel.capacity_cbm).toLocaleString()} cbm` : '—'}</b></span>
                      </div>
                    </div>
                    <Button
                      onClick={handlePredictETA}
                      disabled={etaLoading}
                      data-testid="predict-eta-button"
                      className="rounded-sm bg-[#0A2463] hover:bg-[#0A2463]/90 text-white text-xs"
                    >
                      {etaLoading ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" /> : <LocateFixed className="h-3.5 w-3.5 mr-1.5" />}
                      Predict ETA
                    </Button>
                  </div>
                </CardContent>
              </Card>

              {/* Position Card */}
              {selectedVessel.latitude && (
                <Card className="bg-white border border-slate-200 rounded-sm shadow-none">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-semibold text-slate-800 flex items-center gap-2">
                      <Navigation className="h-4 w-4 text-[#007AFF]" />Last Known Position
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="p-4 pt-0">
                    <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                      <div>
                        <p className="text-[10px] uppercase tracking-[0.2em] font-semibold text-slate-500">Latitude</p>
                        <p className="font-mono text-sm font-bold text-slate-900 mt-0.5">{parseFloat(selectedVessel.latitude).toFixed(4)}&deg;</p>
                      </div>
                      <div>
                        <p className="text-[10px] uppercase tracking-[0.2em] font-semibold text-slate-500">Longitude</p>
                        <p className="font-mono text-sm font-bold text-slate-900 mt-0.5">{parseFloat(selectedVessel.longitude).toFixed(4)}&deg;</p>
                      </div>
                      <div>
                        <p className="text-[10px] uppercase tracking-[0.2em] font-semibold text-slate-500">Speed</p>
                        <p className="font-mono text-sm font-bold text-slate-900 mt-0.5">{selectedVessel.speed_knots ? `${parseFloat(selectedVessel.speed_knots).toFixed(1)} kn` : '—'}</p>
                      </div>
                      <div>
                        <p className="text-[10px] uppercase tracking-[0.2em] font-semibold text-slate-500">Heading</p>
                        <p className="font-mono text-sm font-bold text-slate-900 mt-0.5">{selectedVessel.heading ? `${parseFloat(selectedVessel.heading).toFixed(0)}°` : '—'}</p>
                      </div>
                      <div>
                        <p className="text-[10px] uppercase tracking-[0.2em] font-semibold text-slate-500">Destination</p>
                        <p className="text-sm font-bold text-slate-900 mt-0.5">{selectedVessel.destination || '—'}</p>
                      </div>
                    </div>
                    {selectedVessel.last_position_at && (
                      <p className="text-[10px] text-slate-400 font-mono mt-3 border-t border-slate-100 pt-2">
                        Last update: {new Date(selectedVessel.last_position_at).toLocaleString()}
                      </p>
                    )}
                  </CardContent>
                </Card>
              )}

              {/* ETA Prediction */}
              <ETAResult eta={etaResult} />

              {/* Terminals Grid */}
              <Card className="bg-white border border-slate-200 rounded-sm shadow-none">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-semibold text-slate-800 flex items-center gap-2">
                    <Anchor className="h-4 w-4 text-slate-400" />LNG Terminals ({terminals.length})
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-4 pt-0">
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
                    {terminals.map(t => (
                      <div key={t.name} className="flex items-center gap-2 px-3 py-2 border border-slate-100 rounded-sm" data-testid={`terminal-${t.name.replace(/\s/g, '-')}`}>
                        <MapPin className="h-3 w-3 text-[#007AFF] shrink-0" />
                        <div className="min-w-0">
                          <p className="text-xs font-medium text-slate-700 truncate">{t.name}</p>
                          <p className="text-[10px] font-mono text-slate-400">{t.lat.toFixed(2)}, {t.lon.toFixed(2)}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* Recent ETA Predictions */}
              {etaPredictions.length > 0 && (
                <Card className="bg-white border border-slate-200 rounded-sm shadow-none">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-semibold text-slate-800">Recent ETA Predictions</CardTitle>
                  </CardHeader>
                  <CardContent className="p-0">
                    <table className="w-full text-sm" data-testid="eta-predictions-table">
                      <thead>
                        <tr className="border-b border-slate-100">
                          <th className="text-left text-[10px] uppercase tracking-wider text-slate-500 font-semibold px-5 py-2">Vessel</th>
                          <th className="text-left text-[10px] uppercase tracking-wider text-slate-500 font-semibold px-5 py-2">Destination</th>
                          <th className="text-left text-[10px] uppercase tracking-wider text-slate-500 font-semibold px-5 py-2">Predicted ETA</th>
                          <th className="text-right text-[10px] uppercase tracking-wider text-slate-500 font-semibold px-5 py-2">Confidence</th>
                          <th className="text-right text-[10px] uppercase tracking-wider text-slate-500 font-semibold px-5 py-2">Distance</th>
                          <th className="text-left text-[10px] uppercase tracking-wider text-slate-500 font-semibold px-5 py-2">Created</th>
                        </tr>
                      </thead>
                      <tbody>
                        {etaPredictions.slice(0, 10).map(p => (
                          <tr key={p.id} className="border-b border-slate-50 hover:bg-slate-50/50">
                            <td className="px-5 py-2 text-xs font-medium text-slate-800">{p.vessel_name || '—'}</td>
                            <td className="px-5 py-2 text-xs text-slate-600">{p.destination_port}</td>
                            <td className="px-5 py-2 text-xs font-mono text-slate-700">
                              {p.predicted_eta ? new Date(p.predicted_eta).toLocaleString() : '—'}
                            </td>
                            <td className="px-5 py-2 text-right">
                              <Badge variant="outline" className={`rounded-sm text-[9px] font-mono ${
                                parseFloat(p.confidence_pct) >= 80 ? 'border-green-300 text-green-700 bg-green-50' :
                                parseFloat(p.confidence_pct) >= 60 ? 'border-amber-300 text-amber-700 bg-amber-50' :
                                'border-red-300 text-red-700 bg-red-50'
                              }`}>
                                {parseFloat(p.confidence_pct).toFixed(0)}%
                              </Badge>
                            </td>
                            <td className="px-5 py-2 text-right font-mono text-xs text-slate-600">
                              {p.distance_nm ? `${parseFloat(p.distance_nm).toLocaleString()} NM` : '—'}
                            </td>
                            <td className="px-5 py-2 text-[10px] text-slate-400 font-mono">
                              {p.created_at ? new Date(p.created_at).toLocaleString() : '—'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </CardContent>
                </Card>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
